<template>
  <div class="flex items-center header border border-b border-dark-60">
    <div class="flex-0 flex items-center justify-center sport-icon">
      <SportIcon
        class="text-30"
        :sport-type="iconConfig.sportType"
        :map-type="iconConfig.mapType"
      ></SportIcon>
    </div>
    <div class="flex-0">
      <!--  运动名称-->
      <div class="description text-18 text-white leading-normal font-medium flex flex-row justify-start items-center">
        <template v-if="editing">
          <Input
            ref="nameInputRef"
            v-model="editingName"
            class="name-input"
            :placeholder="$t('H1211')"
            :max-length="90"
            :word-length="getStringByte"
            :word-slice="getStringByMaxBytes"
            @blur="saveActivityName"
          ></Input>
        </template>
        <template v-else>
          <span class="line-clamp-1 description-content">
            {{ activityName }}
          </span>
          <i
            v-if="isMyself"
            class="iconfont iconzhongmingmingcheng-icon edit-icon text-12 ml-4 cursor-pointer text-text-2"
            @click="startEditingName"
          ></i>
        </template>
      </div>
      <!--  运动时间地点-->
      <div class="date-time text-12 font-normal text-text-3">
        {{ activityTime }}
      </div>
    </div>
    <div class="flex-1"></div>
    <div class="flex-0 other-info flex flex-row items-center">
      <!--  导出按钮-->
      <ExportDropdown
        v-if="isMyself"
        :label-id="query.labelId"
        :sport-type="query.sportType"
        :file-types="availibleExportFileTypes"
        @close="handleCloseExportPopover"
      >
        <div ref="elementRef">
          <i
            class="iconfont iconicon_jichutubiao_daochushuju export-icon cursor-pointer text-text-2 text-20 hover:text-text-1"
            :title="$t('H4016')"
          >
          </i
        ></div>
      </ExportDropdown>

      <!--  设备列表-->
      <div
        v-if="deviceList.length"
        class="other-info-item device-list flex items-center px-16"
      >
        <div
          v-for="(item, index) in deviceList"
          :key="'device-' + index"
          class="pointer device-item flex flex-row justify-center items-center"
        >
          <Tooltip :content="item.deviceName">
            <img
              :src="item.thumbnail"
              alt=""
            />
          </Tooltip>
        </div>
      </div>

      <!--  头像-->
      <div class="user-avatar block-image mx-16">
        <img
          v-if="avatar"
          :src="avatar"
        />
        <img
          v-else
          :src="assetsRequire('@assets/coros-traininghub-v2/images/personal/avatar.png')"
          alt=""
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { assetsRequire } from '@client/helper/index';
import { Input, Tooltip } from '@arco-design/web-vue';
import { activityDetailStore } from '@client/store/activityDetailStore';
import { computed, nextTick, ref } from 'vue';
import ExportDropdown from '../ExportDropdown.vue';
import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import { initStore as useInitStore } from '@client/store/initStore';
import { useRoute } from 'vue-router';
import { getStringByMaxBytes, getStringByte } from '@client/helper/string';
import SportIcon from '@client/components/sportIcon/index.vue';
import { useI18n } from 'vue-i18n';
import { userStore } from '../../../../store/userStore';
import { getCustomDeviceName } from './util';

const initailStoreRef = useInitStore();
const userStoreRef = userStore();
const activityDetailStoreRef = activityDetailStore();
dayjs.extend(utcPlugin);

const { t } = useI18n();

const route = useRoute();

const elementRef = ref(null);
function handleCloseExportPopover() {
  elementRef.value.click();
}
const nameInputRef = ref(null);
const editingName = ref('');
const editing = ref(false);
const isMyself = computed(() => activityDetailStoreRef.isMyself);
const query = computed(() => {
  return route.query;
});
const hasGps = computed(() => {
  return activityDetailStoreRef.originalActivityDetail?.frequencyList.some((item) => item.gpsLat && item.gpsLon) || false;
});
const availibleExportFileTypes = computed(() => {
  let { sportType } = route.query;

  if (sportType >= 9800 && sportType <= 9820) sportType = 9800;
  if (sportType >= 9900 && sportType <= 9920) sportType = 9900;
  const { activityExportFileTypes } = initailStoreRef;
  if (!activityExportFileTypes[sportType]) return [];
  return activityExportFileTypes[sportType].filter((item) => hasGps.value || ![1, 2].includes(item));
});

// 活动时间格式转换
const activityTime = computed(() => {
  if (!activityDetailStoreRef.topSummary) return '';
  const summary = activityDetailStoreRef.topSummary || {};
  const timestamp = summary.startTimestamp;
  const utcDateTime = dayjs(timestamp * 10).utc();
  // summary.timezone 是 15分钟时区
  const adjustedDateTime = utcDateTime.add(summary.timezone * 15, 'minute');
  const formattedDateTime = adjustedDateTime.format(t('C6004'));
  return formattedDateTime;
});
const activityName = computed(() => activityDetailStoreRef.topSummary?.name?.trim() || t('H1071'));

const avatar = computed(() => activityDetailStoreRef.originalActivityDetail?.userInfo?.avatar);
const iconConfig = computed(() => {
  const { sportType, bitmapType } = activityDetailStoreRef;
  return {
    sportType,
    mapType: bitmapType
  };
});
const deviceList = computed(() => {
  const customNameList = userStoreRef.deviceCustomNameList;
  if (!activityDetailStoreRef.deviceList?.length) return [];

  // 匹配自定义设备名称
  return activityDetailStoreRef.deviceList.map((item) => {
    const customNameItem = getCustomDeviceName(customNameList, item);
    return {
      ...item,
      deviceName: customNameItem
    };
  });
});

// 进入运动名字编辑模式
function startEditingName() {
  editingName.value = activityName.value;
  editing.value = true;
  nextTick(() => {
    nameInputRef.value.focus();
  });
}

function saveActivityName() {
  if (activityName.value.trim() !== editingName.value.trim()) {
    activityDetailStoreRef.saveActivityName(editingName.value);
    activityDetailStoreRef.originalActivityDetail.summary.name = editingName.value;
  }
  editing.value = false;
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
